
import { ShoppingBag, X } from "lucide-react";
import {
  DrawerTrigger,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
  VerticalDrawer,
  VerticalDrawerContent,
} from "@socialflows/shared-lib";
import { CartProductItem } from "./cart-product-item";
import { useCart } from "@/contexts/cart-context";
import { useAccount } from "@/contexts/account-context";
import { Link } from "react-router-dom";

export const CartDrawer = () => {
  const {
    items,
    cartCount,
    cartTotal,
    updateQuantity,
    removeFromCart,
    updateVariants,
  } = useCart();
  const { account } = useAccount();

  const shippingCost = account?.configs?.shippingFee || 7;
  const freeShippingThreshold = account?.configs?.freeShippingThreshold || 0;
  const freeShippingEnabled = freeShippingThreshold > 0;
  const remainingForFreeShipping = freeShippingEnabled
    ? Math.max(0, freeShippingThreshold - cartTotal)
    : Infinity;
  const qualifiesForFreeShipping = freeShippingEnabled && cartTotal >= freeShippingThreshold;
  const hasItems = items.length > 0;

  return (
    <VerticalDrawer>
      <DrawerTrigger asChild>
        <button
          type="button"
          className="relative p-2 text-foreground/80 hover:text-foreground transition-colors duration-200"
        >
          <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
          {cartCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 flex items-center justify-center rounded-full bg-foreground text-background text-[10px] font-medium">
              {cartCount > 9 ? "9+" : cartCount}
            </span>
          )}
        </button>
      </DrawerTrigger>

      <VerticalDrawerContent className="flex flex-col h-full">
        {/* Header */}
        <DrawerHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-lg font-medium">
              Cart ({cartCount})
            </DrawerTitle>
            <DrawerClose className="p-1 text-muted-foreground hover:text-foreground transition-colors">
              <X className="h-5 w-5" strokeWidth={1.5} />
            </DrawerClose>
          </div>
        </DrawerHeader>

        {/* Free Shipping Progress - show when free shipping is enabled */}
        {freeShippingEnabled && (
          <div className="px-6 py-3 bg-muted/30 border-b">
            <div className="flex h-2 overflow-hidden rounded bg-muted text-xs mb-3">
              <div
                style={{
                  width: `${Math.min(100, (cartTotal / freeShippingThreshold) * 100)}%`,
                  backgroundImage:
                    "linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)",
                  animation: "2s linear 0s infinite progress-bar-stripes",
                  transition: ".3s ease-out",
                  transitionProperty: "width,background-color",
                  backgroundSize: "40px 40px",
                }}
                className="h-full text-center text-xs text-white rounded-full bg-green-500"
              />
            </div>
            {qualifiesForFreeShipping ? (
              <div className="text-xs text-green-600 font-medium">
                You qualify for <span className="font-bold">Free Shipping</span>!
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">
                Only{" "}
                <span className="font-semibold text-foreground">
                  {remainingForFreeShipping.toFixed(2)} TND
                </span>{" "}
                away from <span className="font-bold">Free Shipping</span>!
              </div>
            )}
          </div>
        )}

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {hasItems ? (
            <div className="space-y-6">
              {items.map((item) => (
                <CartProductItem
                  key={`${item.id}-${JSON.stringify(item.selectedVariants)}`}
                  item={item}
                  onUpdateQuantity={(q) =>
                    updateQuantity(item.id, q, item.selectedVariants)
                  }
                  onRemove={() => removeFromCart(item.id, item.selectedVariants)}
                  onUpdateVariants={(newVariants) =>
                    updateVariants(item.id, item.selectedVariants, newVariants)
                  }
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <ShoppingBag
                className="h-12 w-12 text-muted-foreground/50 mb-4"
                strokeWidth={1}
              />
              <p className="text-muted-foreground">Your cart is empty</p>
              <DrawerClose asChild>
                <Link
                  to="/"
                  className="mt-4 text-sm text-foreground underline underline-offset-4 hover:no-underline"
                >
                  Continue shopping
                </Link>
              </DrawerClose>
            </div>
          )}
        </div>

        {/* Footer */}
        {hasItems && (
          <DrawerFooter className="px-6 py-4 border-t bg-background">
            {/* Summary */}
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal</span>
                <span>{cartTotal.toFixed(2)} TND</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Shipping</span>
                <span>
                  {qualifiesForFreeShipping
                    ? "Free"
                    : `${shippingCost.toFixed(2)} TND`}
                </span>
              </div>
              <div className="flex justify-between text-base font-medium pt-2 border-t">
                <span>Total</span>
                <span>
                  {(
                    cartTotal + (qualifiesForFreeShipping ? 0 : shippingCost)
                  ).toFixed(2)}{" "}
                  TND
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <DrawerClose asChild>
                <Link
                  to="/checkout"
                  className="flex items-center justify-center w-full h-12 bg-foreground text-background text-sm font-medium hover:bg-foreground/90 transition-colors"
                >
                  Checkout
                </Link>
              </DrawerClose>
              <DrawerClose asChild>
                <Link
                  to="/cart"
                  className="block w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  View cart
                </Link>
              </DrawerClose>
            </div>
          </DrawerFooter>
        )}
      </VerticalDrawerContent>
    </VerticalDrawer>
  );
};
